import { showErrorMessage, showSuccessMessage } from 'components/Helpers/Toasts';
import { mapListedProductType } from 'connectors/products/Products';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    useAddProductToWishlistMutationApi,
    useCleanWishlistMutationApi,
    useRemoveProductFromWishlistMutationApi,
    useSharedWishlistQueryApi,
    useWishlistQueryApi,
} from 'graphql/generated';
import { isB2bDomain } from 'helpers/isDomainLocale';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { ListedProductType } from 'types/product';
import { CombinedError } from 'urql';

export const useWishlist = () => {
    const { t } = useTranslation();

    const { isUserLoggedIn } = useCurrentUserData();
    const [isFetchingPaused, setIsFetchingPaused] = useState(true);

    const updateWishlistUuid = usePersistStore((s) => s.updateWishlistUuid);
    const wishlistUuid = usePersistStore((s) => s.wishlistUuid);
    const authLoading = usePersistStore((s) => s.authLoading);

    const domainConfig = useDomainConfig();

    const [, addProductToWishlist] = useAddProductToWishlistMutationApi();
    const [, removeProductFromWishlist] = useRemoveProductFromWishlistMutationApi();
    const [, cleanWishlist] = useCleanWishlistMutationApi();
    const [{ data, fetching, error }, refetchWishlist] = useWishlistQueryApi({
        variables: { wishlistUuid: isUserLoggedIn ? null : wishlistUuid },
        pause: isFetchingPaused,
    });
    useQueryError(error, error instanceof CombinedError);

    useEffect(() => {
        setIsFetchingPaused((!wishlistUuid && !isUserLoggedIn) || isB2bDomain(domainConfig));
    }, [isUserLoggedIn, wishlistUuid, domainConfig]);

    useEffect(() => {
        if (
            isUserLoggedIn &&
            authLoading !== 'logout-loading' &&
            !fetching &&
            data?.productList &&
            wishlistUuid !== data.productList.uuid
        ) {
            updateWishlistUuid(data.productList.uuid);
        }
    }, [
        authLoading,
        data?.productList,
        data?.productList?.uuid,
        fetching,
        isUserLoggedIn,
        updateWishlistUuid,
        wishlistUuid,
    ]);

    const handleCleanWishlist = async () => {
        const cleanWishlistResult = await cleanWishlist({ wishlistUuid });

        if (cleanWishlistResult.error) {
            showErrorMessage(t('Unable to clean wishlist.'));
        } else {
            showSuccessMessage(t('Wishlist was cleaned.'));
            updateWishlistUuid(null);
            refetchWishlist({ requestPolicy: 'network-only' });
            dispatchBroadcastChannel({ name: 'reloadPage' });
        }
    };

    const handleAddToWishlist = async (productUuid: string) => {
        const addProductToWishlistResult = await addProductToWishlist({
            productUuid,
            wishlistUuid: isUserLoggedIn ? null : wishlistUuid,
        });

        if (addProductToWishlistResult.error) {
            showErrorMessage(t('Unable to add product to wishlist.'));
        } else {
            showSuccessMessage(t('The item has been added to your wishlist.'));
            updateWishlistUuid(addProductToWishlistResult.data?.AddProductToList.uuid ?? null);
            refetchWishlist({ requestPolicy: 'network-only' });
            dispatchBroadcastChannel({ name: 'reloadPage' });
        }
    };

    const handleRemoveFromWishlist = async (productUuid: string) => {
        const removeProductFromWishlistResult = await removeProductFromWishlist({ productUuid, wishlistUuid });

        if (removeProductFromWishlistResult.error) {
            showErrorMessage(t('Unable to remove product from wishlist.'));
        } else {
            if (!removeProductFromWishlistResult.data?.RemoveProductFromList) {
                updateWishlistUuid(null);
            }
            showSuccessMessage(t('The item has been removed from your wishlist.'));
            refetchWishlist({ requestPolicy: 'network-only' });
            dispatchBroadcastChannel({ name: 'reloadPage' });
        }
    };

    const isProductInWishlist = (productUuid: string) =>
        !!data?.productList?.products.find((product) => product.uuid === productUuid);

    const toggleProductInWishlist = (productUuid: string) => {
        if (isProductInWishlist(productUuid)) {
            handleRemoveFromWishlist(productUuid);
        } else {
            handleAddToWishlist(productUuid);
        }
    };

    return {
        wishlist: data?.productList || undefined,
        fetching,
        isProductInWishlist,
        handleCleanWishlist,
        toggleProductInWishlist,
    };
};

export const useSharedWishlist = (catnums: string[]): { products: ListedProductType[]; fetching: boolean } => {
    const { currencyCode } = useDomainConfig();
    const [{ data, fetching, error }] = useSharedWishlistQueryApi({
        variables: { catnums },
    });
    useQueryError(error, error instanceof CombinedError);
    if (!data?.productsByCatnums) {
        return { products: [], fetching };
    }
    const sharedProducts = [];
    for (const productData of data.productsByCatnums) {
        sharedProducts.push(mapListedProductType(productData, currencyCode));
    }

    return {
        products: sharedProducts,
        fetching,
    };
};
